/**
 * Click/expand beacons for the Snabbsök implicit-feedback log, shared by the
 * chat's Snabbsök card (snabb-sok.tsx) and the navbar search dialog
 * (search-dialog.tsx). Both log impressions through /resources/snabbsok and
 * clicks through /resources/snabbsok-click, so the rows join the same way
 * regardless of surface (`location` on the impression tells them apart).
 */
import { generateUUID } from '#app/utils/chat-query.js'
import { isTestingMode } from '#app/utils/test-mode.client.js'

/**
 * One search request against /resources/snabbsok: the query, the region and
 * the impression-log params. A fresh impressionId per request (each response
 * is its own impression); sid/iid/loc are appended ONLY with a real session id
 * (empty on the SSR pass) — the loader's `if (sessionId && impressionId)` guard
 * stays the single source of truth for whether to write the impression row.
 * Callers may append their own params (the card's `minimized`) before
 * fetching. Call inside an effect or handler, never during render:
 * isTestingMode() is undefined in the SSR bundle.
 */
export function snabbsokSearchRequest({
	query,
	region,
	sessionId,
	location,
}: {
	query: string
	region: string
	sessionId: string
	location: string
}) {
	const impressionId = generateUUID()
	const params = new URLSearchParams({ q: query, region })
	if (sessionId) {
		params.set('sid', sessionId)
		params.set('iid', impressionId)
		params.set('loc', location)
	}
	// The header marks the impression row as our own test traffic.
	const headers: HeadersInit | undefined = isTestingMode()
		? { 'x-im-testing': '1' }
		: undefined
	return { params, impressionId, headers }
}

/** The click payload, with the fields both surfaces fill the same way. */
export function snabbsokClickPayload({
	startedAt,
	...fields
}: Omit<
	SnabbsokClickPayload,
	'clickedDocId' | 'timeSinceImpressionMs' | 'testing'
> & {
	/** When the impression's fetch started; null if unknown. */
	startedAt: number | null
}): SnabbsokClickPayload {
	return {
		...fields,
		clickedDocId: null, // SnabbHit has no doc_id yet (Phase 2 work)
		timeSinceImpressionMs: startedAt === null ? null : Date.now() - startedAt,
		...(isTestingMode() ? { testing: true as const } : {}),
	}
}

/**
 * Schema for the click-feedback POST. Matches the zod contract on the server
 * (app/routes/resources+/snabbsok-click.ts) — camelCase, fully strict types.
 * Field semantics are documented on `logSnabbsokClick` in
 * `app/utils/search-feedback-db.server.ts`; reproduced here only as type
 * shape so the call sites stay self-checking at compile time.
 */
export type SnabbsokClickPayload = {
	impressionId: string | null
	sessionId: string
	clickedPosition: number
	clickedUrl: string
	clickedSource: string
	clickedSourceType: 'im' | 'external'
	clickedDocId: string | null
	clickedTopicId: string | null
	isSitelink: boolean
	sitelinkIndex: number | null
	timeSinceImpressionMs: number | null
	region: string | null
	/** Set only in test mode (sendBeacon cannot carry x-im-testing). */
	testing?: true
}

/** Respect Do-Not-Track for the implicit-feedback beacon. Plausible's own
 *  tracker honours DNT internally — we mirror it here so a DNT user generates
 *  ZERO outbound analytics traffic from Snabbsök (neither Plausible nor the
 *  Postgres feedback store). */
function isDoNotTrack(): boolean {
	if (typeof navigator === 'undefined') return false
	// `navigator.doNotTrack` returns '1' when enabled in most browsers; some
	// expose it on `window.doNotTrack` (legacy) or as 'yes'. Coerce defensively.
	const dnt =
		(navigator as any).doNotTrack ??
		(typeof window !== 'undefined' && (window as any).doNotTrack)
	return dnt === '1' || dnt === 'yes'
}

/** Fire-and-forget click beacon. Returns void; failures are swallowed (the
 *  endpoint replies 204 and the browser is about to navigate away anyway). */
export function fireFeedbackClick(payload: SnabbsokClickPayload): void {
	if (typeof navigator === 'undefined') return
	if (isDoNotTrack()) return
	try {
		const blob = new Blob([JSON.stringify(payload)], {
			type: 'application/json',
		})
		navigator.sendBeacon('/resources/snabbsok-click', blob)
	} catch {
		/* sendBeacon unsupported or payload too large — drop on the floor */
	}
}

/** Fire-and-forget beacon flipping the impression's `expanded` flag in the
 *  Postgres feedback store when the user opens the minimized bar. Mirrors
 *  `fireFeedbackClick` (DNT-respecting, swallow-on-failure). Caller guards on a
 *  non-null impressionId, so the payload always carries a real id. */
export function fireFeedbackExpand(impressionId: string): void {
	if (typeof navigator === 'undefined') return
	if (isDoNotTrack()) return
	try {
		const blob = new Blob([JSON.stringify({ impressionId })], {
			type: 'application/json',
		})
		navigator.sendBeacon('/resources/snabbsok-minimized-expand', blob)
	} catch {
		/* sendBeacon unsupported — drop on the floor */
	}
}
